import MapAdapter from "./modules/MapAdapter";
import MapMarkerAdapter from "./modules/MapmarkerAdapter";
import GoogleMapsAPI from "./modules/GoogleMapsAPI";
import Location from "./modules/Location";
import MarkerClustererAdapter from "./modules/MarkerClustererAdapter";
import CustomClusterRenderer from "./modules/CustomClusterRenderer";

// Config
const { apiKey, locations } = window.MAP;
const { themeURL } = window.WP_VARS;
const center = { lat: 39.8097343, lng: -98.555619 }; // US mainland center

// DOM Elements
const mapContainer = document.querySelector(".location-finder__map-container");
const mapElement = document.getElementById("gmap-container");
const searchForm = document.querySelector(".location-finder__form");
const distanceInput = document.querySelector("#max-distace-away-input");
const currentLocationBtn = document.querySelector("#current-location-btn");
const locationListEl = document.querySelector(".location-finder__location-list");
const loadMoreButtonTemplate = document.querySelector("#load-more-button").content.firstElementChild;
const searchLocationsButton = document.querySelector("#search-locations-button");
const zipInput = document.querySelector("#zip-code-input");

// Safari focus fixes
distanceInput.addEventListener("blur", () => searchLocationsButton.focus());
searchLocationsButton.addEventListener("blur", () => currentLocationBtn.focus());

// State
const markers = [];
let visibleLocationsCursor = 0;
let focusedLocation = null;

/* -------------------------- Set Focused Location -------------------------- */
const setFocusedLocation = (mapLocation) => {
  if (focusedLocation) {
    focusedLocation.closeInfoWindow();
    focusedLocation.unFocusListItem();
  }
  focusedLocation = mapLocation;
  focusedLocation.focusListItem();
  focusedLocation.openInfoWindow();
};

/* ------------------------------- Loading UI ------------------------------- */
const startLoading = () => mapContainer.classList.add("location-finder__map-container--loading");
const stopLoading = () => mapContainer.classList.remove("location-finder__map-container--loading");

/* ------------------------------- Create Marker ---------------------------- */
const createMarker = ({ lat, lng }) =>
  new MapMarkerAdapter({
    lat: parseFloat(lat),
    lng: parseFloat(lng),
    icon: {
      url: themeURL + "/icons/marker.svg",
      scaledSize: new google.maps.Size(24, 33),
    },
  });

/* ---------------------------- Render Locations ---------------------------- */
const renderLocations = (locations) => {
  const locationElements = locations.map((l) => {
    const locationElement = l.getView();
    locationElement.classList.add("location-finder__location-item--visible");
    return locationElement;
  });
  locationListEl.prepend(...locationElements);
  locationListEl.scrollTo({ top: 0, behavior: "smooth" });
};

/* ------------------------- Generate Load More Button ----------------------- */
const generateLoadMoreButton = () => {
  const existingBtn = document.querySelector(".location-finder__load-more-item");
  if (existingBtn) existingBtn.style.display = "none";

  const itemEl = loadMoreButtonTemplate.cloneNode(true);
  const buttonEl = itemEl.querySelector(".button");
  buttonEl.addEventListener("click", loadMoreLocations);
  locationListEl.append(itemEl);
};

/* --------------------------- Load More Locations --------------------------- */
const loadMoreLocations = () => {
  for (let i = 0; i < 10; i++) {
    const nextItem = locationListEl.children.item(visibleLocationsCursor);
    if (!nextItem) break;
    nextItem.classList.add("location-finder__location-item--visible");
    visibleLocationsCursor++;
  }

  const hasMore = !!locationListEl.children.item(visibleLocationsCursor);
  hasMore ? generateLoadMoreButton() : document.querySelector(".location-finder__load-more-item")?.remove();
};

/* -------------------------- Clear Location List --------------------------- */
const emptyLocationList = () => {
  locationListEl.innerHTML = "";
};

/* ------------------------ Sort Locations by Distance ----------------------- */
const sortLocationsByDistance = (locations) =>
  locations.sort((a, b) => {
    const aHasISchedule = !!a._ischedule_url;
    const bHasISchedule = !!b._ischedule_url;

    if (window.ENABLE_ISCHEDULE) {
      if (aHasISchedule && !bHasISchedule) return -1;
      if (!aHasISchedule && bHasISchedule) return 1;
    }

    return parseFloat(a.distance) - parseFloat(b.distance);
  });

/* -------------------------- Find Closest Markers -------------------------- */
const findClosestMarkers = (coordinates, maxDistance, cm, mapApi) => {
  emptyLocationList();
  cm.clearMarkers();

  const activeMarkers = [];
  const activeLocations = [];

  markers.forEach((m) => {
    const latLng = new google.maps.LatLng(m.coordinates.lat, m.coordinates.lng);
    const distance = mapApi.distanceBetween(latLng, coordinates);

    if (distance <= maxDistance) {
      m.distance = distance;
      m.activate();
      activeMarkers.push(m.marker);
      activeLocations.push(m);
    } else {
      m.deactivate();
    }
  });

  const sortedLocations = sortLocationsByDistance(activeLocations);
  renderLocations(sortedLocations);
  cm.addMarkers(activeMarkers);

  return activeLocations;
};

/* --------------------------- Get User Location ---------------------------- */
const getUserLocation = () =>
  new Promise((resolve, reject) => {
    const stored = localStorage.getItem("location");
    if (stored) return resolve(JSON.parse(stored));

    if (!navigator.geolocation) return reject(new Error("Geolocation not supported."));

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        localStorage.setItem("location", JSON.stringify(coords));
        resolve(coords);
      },
      (e) => reject(e)
    );
  });

/* ---------------------------- Init Map Script ----------------------------- */
window.initMap = () => {
  const mapApi = new GoogleMapsAPI(apiKey);
  const map = new MapAdapter({ container: mapElement, center, zoom: 5 });
  const cm = new markerClusterer.MarkerClusterer({
    markers: [],
    map: map.getInstance(),
    renderer: new CustomClusterRenderer(),
  });

  /* ---------------------------- Fit Bounds Helper ---------------------------- */
  const fitBounds = (locations) => {
    const bounds = new google.maps.LatLngBounds();
    locations.forEach((loc) => bounds.extend(loc.marker.getPosition()));
    map.getInstance().setCenter(bounds.getCenter());
    map.getInstance().fitBounds(bounds);
  };

  map.init();

  /* -------------------------- Create Location Objects ------------------------- */
  locations.forEach((data) => {
    const locationItem = new Location({ ...data, map: map.getInstance() });
    locationItem.infoWindow = new google.maps.InfoWindow({
      content: locationItem.getPopupView(),
    });

    const marker = createMarker(data.coordinates);
    locationItem.marker = marker.getInstance();

    marker.click(() => setFocusedLocation(locationItem));
    locationItem.onListItemClick(() => {
      setFocusedLocation(locationItem);
      map.getInstance().setZoom(13);
      map.getInstance().setCenter(marker.getInstance().getPosition());
    });

    markers.push(locationItem);
  });

  renderLocations(markers);
  setTimeout(loadMoreLocations, 0);

  /* ---------------------- Update Map with User Location ---------------------- */
  const updateMapWithUserLocation = async () => {
    setTimeout(() => {
      if (!localStorage.getItem("location")) stopLoading();
    }, 4000);

    startLoading();
    const { latitude, longitude } = await getUserLocation();
    const latLng = new google.maps.LatLng(latitude, longitude);

    const results = await mapApi.geocodeLatLng(latLng);
    if (results.length) {
      const zipComponent = results[0].address_components.find(c => c.types.includes("postal_code"));
      if (zipComponent) {
        const zip = zipComponent.long_name;
        document.cookie = `zip=${encodeURIComponent(zip)}; path=/; max-age=${60 * 60 * 24 * 30}`;
        zipInput.value = zip; // auto-fill ZIP input
      }
    }

    fitBounds(findClosestMarkers(latLng, parseInt(distanceInput.value), cm, mapApi));
    stopLoading();
  };

  /* ---------------------------- Event Listeners ----------------------------- */
  currentLocationBtn.addEventListener("click", updateMapWithUserLocation);

  searchForm.addEventListener("submit", async (e) => {
    e.preventDefault();
    const zip = e.target.elements.zip.value;
    const maxDistance = parseInt(e.target.elements.distance.value);

    document.cookie = `zip=${encodeURIComponent(zip)}; path=/; max-age=${60 * 60 * 24 * 30}`;

    const results = await mapApi.geocodeAddress(`${zip}, USA`);
    if (results.length) {
      const coordinates = results[0].geometry.location;
      fitBounds(findClosestMarkers(coordinates, maxDistance, cm, mapApi));
    }
  });

  /* ------------------- Auto ZIP Search or Fallback to Geo ------------------- */
  const cookieZip = document.cookie.split("; ").find(row => row.startsWith("zip="));
  if (cookieZip) {
    const zip = decodeURIComponent(cookieZip.split("=")[1]);
    zipInput.value = zip;

    (async () => {
      const results = await mapApi.geocodeAddress(`${zip}, USA`);
      if (results.length) {
        const coordinates = results[0].geometry.location;
        const maxDistance = parseInt(distanceInput.value);
        fitBounds(findClosestMarkers(coordinates, maxDistance, cm, mapApi));
      }
    })();
  } else {
    updateMapWithUserLocation();
  }
};
